import { useEffect, useState } from 'react'
import { getAccount } from '../../api/accounts'
import { getCase } from '../../api/cases'
import { getFiscalYear } from '../../api/fiscalYears'
import { getProduction } from '../../api/film'
import { getLegalEntity } from '../../api/legalEntities'

export interface CaseContext {
  kind: 'rnd' | 'film'
  accountName: string | null
  legalEntityName: string | null
  /** The Production title for a film Case, or a generic "R&D case" label — never a raw UUID. */
  caseLabel: string
  fiscalYearId: string | null
  /** `FiscalYear.label` (e.g. `"FY2025"`) via `GET /fiscal-years/{id}` — `null` only when the id
   * itself is `null` or the lookup fails, never a raw UUID standing in for it. */
  fiscalYearLabel: string | null
}

/**
 * Resolves Account · Legal entity · Case · Fiscal year context for a set of `caseId`s, client-side
 * (CLAUDE.md §4: "every cross-case list carries Account · Legal entity · Case · Fiscal year").
 *
 * `task.api.TaskApi`/`TaskView` deliberately does NOT denormalize this (disclosed gap, see
 * `module.task`'s README): `case_.api.CaseApi` has no `CaseListView`-shaped single-id read, and
 * adding one is a `module.case_` change outside that module's assigned path. This hook instead
 * does the SAME "batched, distinct-id, resolve-once" work `CaseService.toListViews` does on the
 * backend for the Cases list — just client-side, via the already-published `GET /cases/{id}`,
 * `GET /accounts/{id}`, `GET /legal-entities/{id}`, and `GET /productions/{id}` endpoints. A
 * failed/missing lookup degrades to `null`/a generic label, never a thrown render error (every
 * caller already wraps its tree in a `WidgetErrorBoundary`).
 */
export function useCaseContexts(caseIds: (string | null)[]): Map<string, CaseContext> {
  const [contexts, setContexts] = useState<Map<string, CaseContext>>(new Map())
  const key = Array.from(new Set(caseIds.filter((id): id is string => Boolean(id)))).sort().join(',')

  useEffect(() => {
    const distinctIds = key ? key.split(',') : []
    if (distinctIds.length === 0) {
      setContexts(new Map())
      return
    }
    let cancelled = false
    void Promise.all(
      distinctIds.map(async (caseId): Promise<[string, CaseContext] | null> => {
        try {
          const caseView = await getCase(caseId)
          const [accountResult, legalEntityResult, production, fiscalYear] = await Promise.all([
            getAccount(caseView.accountId).catch(() => null),
            getLegalEntity(caseView.legalEntityId).catch(() => null),
            caseView.kind === 'film' && caseView.productionId
              ? getProduction(caseView.productionId).catch(() => null)
              : Promise.resolve(null),
            caseView.fiscalYearId ? getFiscalYear(caseView.fiscalYearId).catch(() => null) : Promise.resolve(null),
          ])
          return [
            caseId,
            {
              kind: caseView.kind,
              accountName: accountResult?.account.name ?? null,
              legalEntityName: legalEntityResult?.entity.name ?? null,
              caseLabel: production?.title ?? (caseView.kind === 'film' ? 'Film case' : 'R&D case'),
              fiscalYearId: caseView.fiscalYearId,
              fiscalYearLabel: fiscalYear?.label ?? null,
            },
          ]
        } catch {
          return null
        }
      }),
    ).then((entries) => {
      if (cancelled) return
      const next = new Map<string, CaseContext>()
      for (const entry of entries) {
        if (entry) next.set(entry[0], entry[1])
      }
      setContexts(next)
    })
    return () => {
      cancelled = true
    }
    // `key` is the stable, sorted, de-duplicated dependency — deliberately not `caseIds` itself
    // (a new array identity every render would otherwise re-fetch on every keystroke).
  }, [key])

  return contexts
}
